/**
 * CSV serialization hardened against spreadsheet formula injection.
 *
 * A cell whose (whitespace-trimmed) value begins with `=`, `+`, `-`, `@`, tab
 * or CR is treated by Excel/Sheets as a formula. User-controlled text (names,
 * emails, company names) is neutralised by prefixing a single quote so the value
 * is shown literally. Fields containing commas, quotes or newlines are quoted,
 * and inner quotes are doubled per RFC 4180.
 */

const FORMULA_TRIGGERS = ['=', '+', '-', '@', '\t', '\r'];

const neutralizeFormula = (value: string): string => {
  const trimmed = value.replace(/^[\s]+/, '');
  return FORMULA_TRIGGERS.some((trigger) => trimmed.startsWith(trigger))
    ? `'${value}`
    : value;
};

const escapeField = (value: string | number | boolean): string => {
  const raw = neutralizeFormula(String(value));
  const needsQuoting = /[",\r\n]/.test(raw);
  const escaped = raw.replace(/"/g, '""');
  return needsQuoting ? `"${escaped}"` : escaped;
};

/** Build a CSV string from a header row and data rows (CRLF line endings). */
export const toCsv = (
  headers: string[],
  rows: Array<Array<string | number | boolean>>
): string =>
  [headers, ...rows]
    .map((row) => row.map(escapeField).join(','))
    .join('\r\n');

/** Trigger a browser download of `content` as a UTF-8 CSV file. */
export const downloadCsv = (fileName: string, content: string): void => {
  // BOM so Excel reads UTF-8 correctly.
  const blob = new Blob(['﻿', content], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
